// frontend/src/pages/People.tsx
// UPDATED: Fixed asset value parsing, risk score calculations, and platform-wide thresholds

import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  Paper,
  LinearProgress,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Slider,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  Home as HomeIcon,
  LocalHospital as HospitalIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationOnIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  DirectionsCar as CarIcon,
  Phone as PhoneIcon,
  Dashboard as DashboardIcon,
  Map as MapIcon,
  HelpOutline as HelpIcon,
  Dangerous as DangerousIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircle as CheckCircleIcon,
  MenuBook as MethodologyIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ScatterChart,
  Scatter,
  ZAxis,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PortfolioItem {
  postal_code?: string;
  city?: string;
  state?: string;
  asset_value?: number | string;
  risk_score?: number | string;
  
  // Resilience Scores
  resilience?: number;
  com_risk?: number;
  
  // Community Capital
  ccoc?: number;  // Creative class %
  cco?: number;   // Creative class percentile
  
  // Economic Resilience
  non_agri_pop_p?: number;  // Non-agricultural %
  non_agri_pop?: number;    // Non-agricultural percentile
  lg_sm_biz_ratio?: number; // Large/small business ratio
  e_serv_in_p?: number;     // Service industry %
  e_serv_in?: number;       // Service industry percentile
  
  // Environmental / Income
  low_income_p?: number;    // Low income %
  low_income?: number;      // Low income percentile
  
  // Housing
  homeowner_p?: number;
  homeowner?: number;
  non_mobile_p?: number;    // Non-mobile housing %
  non_mobile?: number;      // Non-mobile percentile
  newer_housing?: number;
  people_unit_n?: number;
  people_unit?: number;
  low_inc_hsg_tract?: number;   // Low income housing tracts
  treas_opp_zone_tract?: number; // Treasury opportunity zones
  
  // Healthcare
  hosp_bed_10k_n?: number;
  hosp_bed_10k?: number;
  hosp_capita_n?: number;   // Hospitals per capita
  hosp_capita?: number;     // Hospitals per capita percentile
  
  // Special Needs
  nhp_cap_n?: number;       // Nursing home per capita
  nhp_cap?: number;         // Nursing home percentile
  abled_p?: number;         // Non-disabled %
  abled?: number;           // Non-disabled percentile
  hispanic_unins_p?: number;
  hispanic_unins?: number;
  
  // Demographics - Age/Gender
  non_elder_p?: number;
  non_elder?: number;
  female_p?: number;
  female?: number;
  
  // Demographics - Race/Ethnicity
  non_white_p?: number;     // People of color %
  non_white?: number;       // People of color percentile
  black_p?: number;
  black?: number;
  asian_p?: number;
  asian?: number;
  native_amer_p?: number;
  native_amer?: number;
  
  // Communication/Language
  linquis_iso_p?: number;   // Linguistic isolation %
  linquis_iso?: number;     // Linguistic isolation percentile
  tele_p?: number;
  tele?: number;
  
  // Infrastructure
  vehicle?: number;
  hotels_km_n?: number;     // Hotels per km
  hotels_km?: number;       // Hotels percentile
  pub_schls_mi_n?: number;  // Public schools per mi
  pub_schls_mi?: number;    // Public schools percentile
  
  // Institutional
  govt_spec_d_n?: number;   // Govt/special districts
  govt_spec_d?: number;     // Govt districts percentile
  pd_dis_dec_n?: number;    // Paid disaster declarations
  pd_dis_dec?: number;      // Disaster declarations percentile
  nfipp?: number;           // NFIP coverage %
  nfip?: number;            // NFIP percentile
  
  // Community
  civ_10k?: number;
  
  [key: string]: any;
}

interface PortfolioData {
  id: string;
  name: string;
  items: PortfolioItem[];
  totalValue?: number;
  total_value?: number;
}

interface RecoveryRisk {
  riskScore: number;
  factors: Array<{ factor: string; severity: string; value: string }>;
  recoveryCategory: 'unlikely' | 'difficult' | 'challenging' | 'expected';
}

interface WhatIfAssumptions {
  resilience: number;
  vulnerability: number;
  homeownership: number;
  hospitalBeds: number;
  teleAccess: number;
  vehicleAccess: number;
  housingQuality: number;
}

// ============================================================================
// UTILITY FUNCTIONS - Safe Number Parsing
// ============================================================================

// Parse asset value from various formats (handles "$1,234,567", "1234567", 1234567)
const parseAssetValue = (val: any): number => {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    // Remove $, commas, and whitespace
    const cleaned = val.replace(/[$,\s]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Safe number extraction for any field
const safeNumber = (val: any, defaultVal: number = 0): number => {
  if (val === null || val === undefined || val === '') return defaultVal;
  const num = Number(val);
  return isNaN(num) ? defaultVal : num;
};

// Format currency for display
const formatCurrency = (val: any): string => {
  const num = parseAssetValue(val);
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  if (num === 0) return '-';
  return `$${num.toFixed(0)}`;
};

// ============================================================================
// METRIC CONFIGURATION - Comprehensive People Metrics
// ============================================================================

const METRICS_CONFIG = [
  // === FINANCIAL ===
  { 
    column_name: 'asset_value', 
    alias: 'Asset Value', 
    tooltip: 'Total real estate value of the property or location.',
    category: 'financial', 
    format: 'currency',
    icon: <HomeIcon /> 
  },
  
  // === RESILIENCE SCORES ===
  { 
    column_name: 'resilience', 
    alias: 'Community Resilience Score', 
    tooltip: 'Overall community resilience rating (1-10 scale). Higher scores indicate better disaster preparedness and recovery capacity.',
    category: 'resilience', 
    format: 'score',
    icon: <SecurityIcon /> 
  },
  { 
    column_name: 'com_risk', 
    alias: 'Community Vulnerability Score', 
    tooltip: 'Overall community vulnerability rating (1-10 scale). Higher scores indicate greater vulnerability to disasters.',
    category: 'vulnerability', 
    format: 'score',
    icon: <WarningIcon /> 
  },

  // === COMMUNITY CAPITAL ===
  { 
    column_name: 'ccoc', 
    alias: 'Creative Class Employment %', 
    tooltip: 'Percent population employed in creative class occupations. Higher values indicate stronger economic diversity and innovation capacity.',
    category: 'economic', 
    format: 'percentage'
  },
  { 
    column_name: 'cco', 
    alias: 'Creative Class Percentile', 
    tooltip: 'National percentile for creative class employment.',
    category: 'economic', 
    format: 'percentile'
  },

  // === ECONOMIC RESILIENCE ===
  { 
    column_name: 'non_agri_pop_p', 
    alias: 'Non-Agricultural Employment %', 
    tooltip: 'Percent population NOT employed in farming, fishing, forestry, and extractive industries. Higher values indicate economic diversity.',
    category: 'economic', 
    format: 'percentage'
  },
  { 
    column_name: 'non_agri_pop', 
    alias: 'Non-Agricultural Percentile', 
    tooltip: 'National percentile for non-agricultural employment.',
    category: 'economic', 
    format: 'percentile'
  },
  { 
    column_name: 'lg_sm_biz_ratio', 
    alias: 'Large/Small Business Ratio', 
    tooltip: 'Ratio of large to small businesses. Higher ratios may indicate economic stability but less small business diversity.',
    category: 'economic', 
    format: 'number'
  },
  { 
    column_name: 'e_serv_in_p', 
    alias: 'Service Industry Employment %', 
    tooltip: 'Percent of employment in service industry.',
    category: 'economic', 
    format: 'percentage'
  },
  { 
    column_name: 'e_serv_in', 
    alias: 'Service Industry Percentile', 
    tooltip: 'National percentile for service industry employment.',
    category: 'economic', 
    format: 'percentile'
  },

  // === ENVIRONMENTAL / INCOME ===
  { 
    column_name: 'low_income_p', 
    alias: 'Low-Income Population %', 
    tooltip: 'Percentage of population classified as low-income. Higher values indicate greater economic vulnerability.',
    category: 'demographics', 
    format: 'percentage',
    icon: <WarningIcon />
  },
  { 
    column_name: 'low_income', 
    alias: 'Low-Income Percentile', 
    tooltip: 'National percentile for low-income population.',
    category: 'demographics', 
    format: 'percentile'
  },

  // === HOUSING ===
  { 
    column_name: 'homeowner_p', 
    alias: 'Homeownership Rate', 
    tooltip: 'Percentage of housing units that are owner-occupied.',
    category: 'housing', 
    format: 'percentage',
    icon: <HomeIcon /> 
  },
  { 
    column_name: 'homeowner', 
    alias: 'Homeownership Percentile', 
    tooltip: 'National percentile ranking for homeownership rate.',
    category: 'housing', 
    format: 'percentile' 
  },
  { 
    column_name: 'non_mobile_p', 
    alias: 'Non-Mobile Housing %', 
    tooltip: 'Percent housing units that are NOT mobile homes. Mobile homes are more vulnerable to disasters.',
    category: 'housing', 
    format: 'percentage'
  },
  { 
    column_name: 'non_mobile', 
    alias: 'Non-Mobile Housing Percentile', 
    tooltip: 'National percentile for non-mobile housing.',
    category: 'housing', 
    format: 'percentile'
  },
  { 
    column_name: 'newer_housing', 
    alias: 'Newer Housing Percentile', 
    tooltip: 'National percentile for housing built after 1970 - newer housing typically more resilient.',
    category: 'housing', 
    format: 'percentile' 
  },
  { 
    column_name: 'people_unit_n', 
    alias: 'People per Housing Unit', 
    tooltip: 'Average number of people per housing unit. Higher values may indicate overcrowding.',
    category: 'housing', 
    format: 'number' 
  },
  { 
    column_name: 'people_unit', 
    alias: 'People per Unit Percentile', 
    tooltip: 'National percentile ranking for housing density.',
    category: 'housing', 
    format: 'percentile' 
  },
  { 
    column_name: 'low_inc_hsg_tract', 
    alias: 'Low Income Housing Tracts', 
    tooltip: 'Number of Low Income Housing Tracts in the area.',
    category: 'housing', 
    format: 'number'
  },
  { 
    column_name: 'treas_opp_zone_tract', 
    alias: 'Treasury Opportunity Zones', 
    tooltip: 'Number of Treasury Opportunity Zone tracts - areas designated for economic development.',
    category: 'housing', 
    format: 'number'
  },

  // === HEALTHCARE ===
  { 
    column_name: 'hosp_bed_10k_n', 
    alias: 'Hospital Beds per 10K', 
    tooltip: 'Number of hospital beds available per 10,000 population.',
    category: 'healthcare', 
    format: 'number',
    icon: <HospitalIcon /> 
  },
  { 
    column_name: 'hosp_bed_10k', 
    alias: 'Hospital Beds Percentile', 
    tooltip: 'National percentile ranking for hospital bed availability.',
    category: 'healthcare', 
    format: 'percentile' 
  },
  { 
    column_name: 'hosp_capita_n', 
    alias: 'Hospitals Per Capita', 
    tooltip: 'Hospitals per capita at county level.',
    category: 'healthcare', 
    format: 'number'
  },
  { 
    column_name: 'hosp_capita', 
    alias: 'Hospitals Per Capita Percentile', 
    tooltip: 'National percentile for hospitals per capita.',
    category: 'healthcare', 
    format: 'percentile'
  },

  // === SPECIAL NEEDS POPULATIONS ===
  { 
    column_name: 'nhp_cap_n', 
    alias: 'Nursing Home Residents/Capita', 
    tooltip: 'Nursing home residents per capita. Higher values indicate more vulnerable populations requiring evacuation assistance.',
    category: 'healthcare', 
    format: 'number'
  },
  { 
    column_name: 'nhp_cap', 
    alias: 'Nursing Home Percentile', 
    tooltip: 'National percentile for nursing home residents per capita.',
    category: 'healthcare', 
    format: 'percentile'
  },
  { 
    column_name: 'abled_p', 
    alias: 'Non-Disabled Population %', 
    tooltip: 'Percent population WITHOUT a sensory, physical, or mental disability.',
    category: 'demographics', 
    format: 'percentage'
  },
  { 
    column_name: 'abled', 
    alias: 'Non-Disabled Percentile', 
    tooltip: 'National percentile for population without disability.',
    category: 'demographics', 
    format: 'percentile'
  },
  { 
    column_name: 'hispanic_unins_p', 
    alias: 'Hispanic Population %', 
    tooltip: 'Percent Hispanic population (health insurance context).',
    category: 'demographics', 
    format: 'percentage'
  },
  { 
    column_name: 'hispanic_unins', 
    alias: 'Hispanic Percentile', 
    tooltip: 'National percentile for Hispanic population.',
    category: 'demographics', 
    format: 'percentile'
  },

  // === DEMOGRAPHICS - AGE/GENDER ===
  { 
    column_name: 'non_elder_p', 
    alias: 'Non-Elderly Population %', 
    tooltip: 'Percentage of population under 65 years old. Higher values indicate younger, more mobile population.',
    category: 'demographics', 
    format: 'percentage' 
  },
  { 
    column_name: 'non_elder', 
    alias: 'Non-Elderly Percentile', 
    tooltip: 'National percentile ranking for non-elderly population.',
    category: 'demographics', 
    format: 'percentile' 
  },
  { 
    column_name: 'female_p', 
    alias: 'Female Population %', 
    tooltip: 'Percent of population that is female.',
    category: 'demographics', 
    format: 'percentage'
  },
  { 
    column_name: 'female', 
    alias: 'Female Percentile', 
    tooltip: 'National percentile for female population.',
    category: 'demographics', 
    format: 'percentile'
  },

  // === DEMOGRAPHICS - RACE/ETHNICITY ===
  { 
    column_name: 'non_white_p', 
    alias: 'People of Color %', 
    tooltip: 'Percentage of people of color in the population.',
    category: 'demographics', 
    format: 'percentage'
  },
  { 
    column_name: 'non_white', 
    alias: 'People of Color Percentile', 
    tooltip: 'National percentile for people of color.',
    category: 'demographics', 
    format: 'percentile'
  },
  { 
    column_name: 'black_p', 
    alias: 'Black Population %', 
    tooltip: 'Percent Black population.',
    category: 'demographics', 
    format: 'percentage'
  },
  { 
    column_name: 'black', 
    alias: 'Black Percentile', 
    tooltip: 'National percentile for Black population.',
    category: 'demographics', 
    format: 'percentile'
  },
  { 
    column_name: 'asian_p', 
    alias: 'Asian Population %', 
    tooltip: 'Percent Asian population.',
    category: 'demographics', 
    format: 'percentage'
  },
  { 
    column_name: 'asian', 
    alias: 'Asian Percentile', 
    tooltip: 'National percentile for Asian population.',
    category: 'demographics', 
    format: 'percentile'
  },
  { 
    column_name: 'native_amer_p', 
    alias: 'Native American %', 
    tooltip: 'Percent Native American population.',
    category: 'demographics', 
    format: 'percentage'
  },
  { 
    column_name: 'native_amer', 
    alias: 'Native American Percentile', 
    tooltip: 'National percentile for Native American population.',
    category: 'demographics', 
    format: 'percentile'
  },

  // === COMMUNICATION/LANGUAGE ===
  { 
    column_name: 'linquis_iso_p', 
    alias: 'Linguistic Isolation %', 
    tooltip: 'Percentage of households in linguistic isolation. Higher values indicate communication barriers during emergencies.',
    category: 'infrastructure', 
    format: 'percentage'
  },
  { 
    column_name: 'linquis_iso', 
    alias: 'Linguistic Isolation Percentile', 
    tooltip: 'National percentile for linguistic isolation.',
    category: 'infrastructure', 
    format: 'percentile'
  },
  { 
    column_name: 'tele_p', 
    alias: 'Telephone Access %', 
    tooltip: 'Percentage of households with telephone service.',
    category: 'infrastructure', 
    format: 'percentage',
    icon: <PhoneIcon /> 
  },
  { 
    column_name: 'tele', 
    alias: 'Telephone Access Percentile', 
    tooltip: 'National percentile ranking for telephone access.',
    category: 'infrastructure', 
    format: 'percentile' 
  },

  // === INFRASTRUCTURE RESILIENCE ===
  { 
    column_name: 'vehicle', 
    alias: 'Vehicle Access Percentile', 
    tooltip: 'National percentile ranking for vehicle ownership - important for evacuation capability.',
    category: 'infrastructure', 
    format: 'percentile',
    icon: <CarIcon /> 
  },
  { 
    column_name: 'hotels_km_n', 
    alias: 'Hotels per Sq Km', 
    tooltip: 'Number of hotels/motels per square kilometer. Important for temporary shelter capacity.',
    category: 'infrastructure', 
    format: 'number'
  },
  { 
    column_name: 'hotels_km', 
    alias: 'Hotels Percentile', 
    tooltip: 'National percentile for hotel/motel density.',
    category: 'infrastructure', 
    format: 'percentile'
  },
  { 
    column_name: 'pub_schls_mi_n', 
    alias: 'Public Schools per Sq Mi', 
    tooltip: 'Number of public schools per square mile. Schools often serve as emergency shelters.',
    category: 'infrastructure', 
    format: 'number'
  },
  { 
    column_name: 'pub_schls_mi', 
    alias: 'Public Schools Percentile', 
    tooltip: 'National percentile for public school density.',
    category: 'infrastructure', 
    format: 'percentile'
  },

  // === INSTITUTIONAL RESILIENCE ===
  { 
    column_name: 'govt_spec_d_n', 
    alias: 'Govt/Special Districts', 
    tooltip: 'Number of governments and special districts. More institutional presence can aid recovery coordination.',
    category: 'institutional', 
    format: 'number'
  },
  { 
    column_name: 'govt_spec_d', 
    alias: 'Govt Districts Percentile', 
    tooltip: 'National percentile for government and special districts.',
    category: 'institutional', 
    format: 'percentile'
  },
  { 
    column_name: 'pd_dis_dec_n', 
    alias: 'Paid Disaster Declarations', 
    tooltip: 'Number of paid disaster declarations. Indicates historical disaster experience and FEMA familiarity.',
    category: 'institutional', 
    format: 'number'
  },
  { 
    column_name: 'pd_dis_dec', 
    alias: 'Disaster Declarations Percentile', 
    tooltip: 'National percentile for paid disaster declarations.',
    category: 'institutional', 
    format: 'percentile'
  },
  { 
    column_name: 'nfipp', 
    alias: 'NFIP Coverage %', 
    tooltip: 'Percent housing units covered by National Flood Insurance Program policies. Critical for flood recovery.',
    category: 'institutional', 
    format: 'percentage'
  },
  { 
    column_name: 'nfip', 
    alias: 'NFIP Coverage Percentile', 
    tooltip: 'National percentile for NFIP coverage.',
    category: 'institutional', 
    format: 'percentile'
  },

  // === COMMUNITY ===
  { 
    column_name: 'civ_10k', 
    alias: 'Civic Orgs per 10K', 
    tooltip: 'Number of civic organizations per 10,000 population - indicates community engagement and recovery support networks.',
    category: 'community', 
    format: 'number' 
  },
];

// ============================================================================
// DEFAULT ASSUMPTIONS FOR WHAT-IF
// ============================================================================

const DEFAULT_ASSUMPTIONS: WhatIfAssumptions = {
  resilience: 0,
  vulnerability: 0,
  homeownership: 0,
  hospitalBeds: 0,
  teleAccess: 0,
  vehicleAccess: 0,
  housingQuality: 0
};

// ============================================================================
// TAB PANEL COMPONENT
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box>{children}</Box>}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function People() {
  const [portfolios, setPortfolios] = useState<PortfolioData[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [showWhatIf, setShowWhatIf] = useState(false);
  const [whatIfAssumptions, setWhatIfAssumptions] = useState<WhatIfAssumptions>(DEFAULT_ASSUMPTIONS);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.id || 'demo';
        
        console.log('People: Fetching portfolios for userId:', userId);
        
        const response = await axios.get(`${API_URL}/portfolios`, {
          params: { userId },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('People: Received', response.data?.length || 0, 'portfolios');
        setPortfolios(response.data || []);
      } catch (err) {
        console.error('Error fetching portfolios:', err);
        setError('Failed to load portfolios');
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolios();
  }, []);

  // ============================================================================
  // SELECTED ITEMS COMPUTATION
  // ============================================================================

  const selectedItems = useMemo(() => {
    if (selectedPortfolio === 'all') {
      return portfolios.flatMap(p => p.items || []);
    }
    const portfolio = portfolios.find(p => p.id === selectedPortfolio);
    return portfolio?.items || [];
  }, [portfolios, selectedPortfolio]);

  // ============================================================================
  // AGGREGATED METRICS
  // ============================================================================

  const aggregatedMetrics = useMemo(() => {
    if (!selectedItems.length) return null;
    
    const metrics: Record<string, { sum: number; count: number; min: number; max: number; avg: number }> = {};
    
    METRICS_CONFIG.forEach(config => {
      metrics[config.column_name] = { sum: 0, count: 0, min: Infinity, max: -Infinity, avg: 0 };
    });

    selectedItems.forEach(item => {
      METRICS_CONFIG.forEach(config => {
        // Use parseAssetValue for currency fields, safeNumber for others
        const value = config.format === 'currency' 
          ? parseAssetValue(item[config.column_name])
          : safeNumber(item[config.column_name]);
        
        if (value !== 0 || item[config.column_name] === 0) {
          metrics[config.column_name].sum += value;
          metrics[config.column_name].count += 1;
          metrics[config.column_name].min = Math.min(metrics[config.column_name].min, value);
          metrics[config.column_name].max = Math.max(metrics[config.column_name].max, value);
        }
      });
    });

    Object.keys(metrics).forEach(key => {
      if (metrics[key].count > 0) {
        metrics[key].avg = metrics[key].sum / metrics[key].count;
      }
      if (metrics[key].min === Infinity) metrics[key].min = 0;
      if (metrics[key].max === -Infinity) metrics[key].max = 0;
    });

    return metrics;
  }, [selectedItems]);

  // ============================================================================
  // FORMAT HELPERS
  // ============================================================================

  const safeFormatNumber = (val: any, decimals: number = 2): string => {
    const num = safeNumber(val);
    return num.toFixed(decimals);
  };

  const formatMetricValue = (value: number | undefined, format: string): string => {
    if (value === undefined || value === null) return 'N/A';
    const num = Number(value);
    if (isNaN(num)) return 'N/A';
    switch (format) {
      case 'currency': return formatCurrency(num);
      case 'percentage': return `${num.toFixed(1)}%`;
      case 'percentile': return `${num.toFixed(0)}th %ile`;
      case 'score': return num.toFixed(2);
      case 'number': return num.toFixed(2);
      default: return num.toFixed(2);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Location', 'Portfolio', 'Asset Value', ...METRICS_CONFIG.map(m => m.alias)].join(',');
    const rows: string[] = [];
    
    portfolios.forEach(portfolio => {
      portfolio.items?.forEach(item => {
        const location = `"${item.city || ''}, ${item.state || ''} ${item.postal_code || ''}"`;
        const portfolioName = `"${portfolio.name}"`;
        const assetValue = parseAssetValue(item.asset_value);
        const values = METRICS_CONFIG.map(config => {
          const value = item[config.column_name];
          return value !== undefined ? value : '';
        }).join(',');
        rows.push(`${location},${portfolioName},${assetValue},${values}`);
      });
    });
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `people_metrics_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ============================================================================
  // DISASTER RECOVERY RISK CALCULATION (with what-if support)
  // Using platform-wide thresholds: High > 6, Medium > 3 and  6, Low 03
  // Comprehensive methodology using all available people metrics
  // 
  // RESEARCH BASIS: Studies show low-income communities face greater recovery
  // challenges due to: less insurance coverage, limited savings, barriers to
  // FEMA assistance, and inability to relocate temporarily. See:
  // - Brookings "Hurricanes hit the poor the hardest"
  // - Center for American Progress "FEMA Equity in Disaster Recovery"
  // - IIT/NSF studies on Katrina vs Sandy
  // ============================================================================

  // Get city-level aggregated metrics for nearby postal codes
  const getCityMetrics = (item: PortfolioItem): PortfolioItem => {
    // Find all items in the same city
    const cityItems = selectedItems.filter(
      i => i.city?.toLowerCase() === item.city?.toLowerCase() && 
           i.state?.toLowerCase() === item.state?.toLowerCase()
    );
    
    if (cityItems.length <= 1) return item; // No aggregation needed
    
    // Helper to average a field across city items
    const avgField = (field: string): number => {
      const values = cityItems.map(i => safeNumber(i[field])).filter(v => v > 0);
      return values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : 0;
    };
    
    return {
      ...item,
      // Core resilience (scores 0-10)
      resilience: avgField('resilience') || item.resilience,
      com_risk: avgField('com_risk') || item.com_risk,
      
      // Race/Ethnicity - RAW PERCENTAGES (_p fields)
      non_white_p: avgField('non_white_p') || item.non_white_p,
      black_p: avgField('black_p') || item.black_p,
      native_amer_p: avgField('native_amer_p') || item.native_amer_p,
      hispanic_unins_p: avgField('hispanic_unins_p') || item.hispanic_unins_p,
      
      // Economic - RAW PERCENTAGES and VALUES
      low_income_p: avgField('low_income_p') || item.low_income_p,
      ccoc: avgField('ccoc') || item.ccoc,  // Creative class occupation %
      non_agri_pop_p: avgField('non_agri_pop_p') || item.non_agri_pop_p,
      e_serv_in_p: avgField('e_serv_in_p') || item.e_serv_in_p,
      lg_sm_biz_ratio: avgField('lg_sm_biz_ratio') || item.lg_sm_biz_ratio,
      low_inc_hsg_tract: avgField('low_inc_hsg_tract') || item.low_inc_hsg_tract,
      treas_opp_zone_tract: avgField('treas_opp_zone_tract') || item.treas_opp_zone_tract,
      
      // Institutional - RAW VALUES
      nfipp: avgField('nfipp') || item.nfipp,  // NFIP participation %
      govt_spec_d_n: avgField('govt_spec_d_n') || item.govt_spec_d_n,
      
      // Vulnerable Populations - RAW PERCENTAGES
      linquis_iso_p: avgField('linquis_iso_p') || item.linquis_iso_p,
      abled_p: avgField('abled_p') || item.abled_p,
      nhp_cap_n: avgField('nhp_cap_n') || item.nhp_cap_n,
      non_elder_p: avgField('non_elder_p') || item.non_elder_p,
      female_p: avgField('female_p') || item.female_p,
      civ_10k: avgField('civ_10k') || item.civ_10k,
      
      // Housing - RAW VALUES
      // non_mobile_p removed - field data appears unreliable
      homeowner_p: avgField('homeowner_p') || item.homeowner_p,
      newer_housing: avgField('newer_housing') || item.newer_housing,  // This is a percentile
      people_unit_n: avgField('people_unit_n') || item.people_unit_n,
      
      // Infrastructure - RAW VALUES
      hosp_bed_10k_n: avgField('hosp_bed_10k_n') || item.hosp_bed_10k_n,
      hosp_capita_n: avgField('hosp_capita_n') || item.hosp_capita_n,
      tele_p: avgField('tele_p') || item.tele_p,
      vehicle: avgField('vehicle') || item.vehicle,  // This is a percentile
      hotels_km_n: avgField('hotels_km_n') || item.hotels_km_n,
      pub_schls_mi_n: avgField('pub_schls_mi_n') || item.pub_schls_mi_n,
      
      // Disaster experience
      pd_dis_dec_n: avgField('pd_dis_dec_n') || item.pd_dis_dec_n,
      
      // Keep property-specific values
      asset_value: item.asset_value,
      risk_score: item.risk_score,
      _cityLocationCount: cityItems.length,
    };
  };

  const calculateRecoveryRisk = (item: PortfolioItem, assumptions: WhatIfAssumptions = DEFAULT_ASSUMPTIONS): RecoveryRisk => {
    // Use city-aggregated metrics for community factors
    const cityItem = getCityMetrics(item);
    
    let riskScore = 0;
    let factors: Array<{ factor: string; severity: string; value: string }> = [];

    // Note if using city-aggregated data
    const cityCount = (cityItem as any)._cityLocationCount;
    if (cityCount && cityCount > 1) {
      factors.push({ factor: `City-Aggregated (${cityCount} locations)`, severity: 'low', value: `${cityItem.city}, ${cityItem.state}` });
    }

    // =========================================================================
    // RECOVERY RISK METHODOLOGY - USING RAW VALUES
    // 
    // Uses raw percentages (_p suffix) and raw numbers (_n suffix) for most factors.
    // Only newer_housing and vehicle use percentile fields.
    // 
    // IMPORTANT: We check for valid data (> 0) before applying penalties.
    // Missing/zero data should not trigger risk flags.
    // =========================================================================

    // Helper to check if data is valid (not null/undefined/0)
    const hasValidData = (val: any): boolean => {
      const num = Number(val);
      return !isNaN(num) && num > 0;
    };

    // =========================================================================
    // 1. RACE/ETHNICITY FACTORS (Max 24 pts)
    // Using raw percentages - higher % = higher FEMA disparity risk
    // =========================================================================

    // 1a. PEOPLE OF COLOR (Max 8 pts) - non_white_p
    if (hasValidData(cityItem.non_white_p)) {
      const nonWhitePct = safeNumber(cityItem.non_white_p, 25);
      if (nonWhitePct > 60) {
        riskScore += 8;
        factors.push({ factor: `High Minority Pop (${nonWhitePct.toFixed(1)}%)`, severity: 'high', value: `${nonWhitePct.toFixed(1)}%` });
      } else if (nonWhitePct > 40) {
        riskScore += 4;
        factors.push({ factor: `Above Avg Minority (${nonWhitePct.toFixed(1)}%)`, severity: 'medium', value: `${nonWhitePct.toFixed(1)}%` });
      }
    }

    // 1b. BLACK POPULATION (Max 6 pts) - black_p - highest FEMA aid disparity
    if (hasValidData(cityItem.black_p)) {
      const blackPct = safeNumber(cityItem.black_p, 12);
      if (blackPct > 40) {
        riskScore += 6;
        factors.push({ factor: `High Black Pop (${blackPct.toFixed(1)}%)`, severity: 'high', value: `${blackPct.toFixed(1)}%` });
      } else if (blackPct > 25) {
        riskScore += 3;
        factors.push({ factor: `Above Avg Black Pop (${blackPct.toFixed(1)}%)`, severity: 'medium', value: `${blackPct.toFixed(1)}%` });
      }
    }

    // 1c. NATIVE AMERICAN (Max 6 pts) - native_amer_p - infrastructure gaps
    if (hasValidData(cityItem.native_amer_p)) {
      const nativePct = safeNumber(cityItem.native_amer_p, 1);
      if (nativePct > 15) {
        riskScore += 6;
        factors.push({ factor: `High Native American (${nativePct.toFixed(1)}%)`, severity: 'high', value: `${nativePct.toFixed(1)}%` });
      } else if (nativePct > 5) {
        riskScore += 3;
        factors.push({ factor: `Above Avg Native Am (${nativePct.toFixed(1)}%)`, severity: 'medium', value: `${nativePct.toFixed(1)}%` });
      }
    }

    // 1d. HISPANIC (Max 4 pts) - hispanic_unins_p - language/documentation barriers
    if (hasValidData(cityItem.hispanic_unins_p)) {
      const hispanicPct = safeNumber(cityItem.hispanic_unins_p, 18);
      if (hispanicPct > 50) {
        riskScore += 4;
        factors.push({ factor: `High Hispanic Pop (${hispanicPct.toFixed(1)}%)`, severity: 'medium', value: `${hispanicPct.toFixed(1)}%` });
      } else if (hispanicPct > 30) {
        riskScore += 2;
        factors.push({ factor: `Above Avg Hispanic (${hispanicPct.toFixed(1)}%)`, severity: 'low', value: `${hispanicPct.toFixed(1)}%` });
      }
    }

    // =========================================================================
    // 2. ECONOMIC VULNERABILITY (Max ~35 pts)
    // =========================================================================

    // 2a. LOW INCOME POPULATION (Max 12 pts) - low_income_p
    if (hasValidData(cityItem.low_income_p)) {
      const lowIncomePct = safeNumber(cityItem.low_income_p, 20);
      if (lowIncomePct > 40) {
        riskScore += 12;
        factors.push({ factor: `Very High Low-Income (${lowIncomePct.toFixed(1)}%)`, severity: 'critical', value: `${lowIncomePct.toFixed(1)}%` });
      } else if (lowIncomePct > 25) {
        riskScore += 7;
        factors.push({ factor: `High Low-Income (${lowIncomePct.toFixed(1)}%)`, severity: 'high', value: `${lowIncomePct.toFixed(1)}%` });
      } else if (lowIncomePct > 15) {
        riskScore += 3;
        factors.push({ factor: `Above Avg Low-Income (${lowIncomePct.toFixed(1)}%)`, severity: 'medium', value: `${lowIncomePct.toFixed(1)}%` });
      }
    }

    // 2b. ASSET VALUE (Max 8 pts) - raw value
    const assetValue = parseAssetValue(item.asset_value);
    if (assetValue === 0) {
      factors.push({ factor: 'Asset Value Unknown', severity: 'low', value: 'N/A' });
    } else if (assetValue < 1000000) {
      riskScore += 8;
      factors.push({ factor: `Low Property Value (${formatCurrency(assetValue)})`, severity: 'high', value: formatCurrency(assetValue) });
    } else if (assetValue < 5000000) {
      riskScore += 4;
      factors.push({ factor: `Moderate Property Value (${formatCurrency(assetValue)})`, severity: 'medium', value: formatCurrency(assetValue) });
    }

    // 2c. ECONOMIC DIVERSITY (Max 5 pts) - ccoc, non_agri_pop_p
    const creativePct = safeNumber(cityItem.ccoc, 30);
    const nonAgriPct = safeNumber(cityItem.non_agri_pop_p, 95);
    if (hasValidData(cityItem.ccoc) || hasValidData(cityItem.non_agri_pop_p)) {
      if (creativePct < 15 && nonAgriPct < 85) {
        riskScore += 5;
        factors.push({ factor: `Low Econ Diversity (${creativePct.toFixed(0)}% creative)`, severity: 'high', value: `${creativePct.toFixed(0)}% creative, ${nonAgriPct.toFixed(0)}% non-agri` });
      } else if (creativePct < 20 || nonAgriPct < 90) {
        riskScore += 2;
        factors.push({ factor: `Limited Econ Diversity (${creativePct.toFixed(0)}% creative)`, severity: 'medium', value: `${creativePct.toFixed(0)}% creative` });
      }
    }

    // 2d. SERVICE INDUSTRY (Max 4 pts) - e_serv_in_p
    if (hasValidData(cityItem.e_serv_in_p)) {
      const servicePct = safeNumber(cityItem.e_serv_in_p, 40);
      if (servicePct > 70) {
        riskScore += 4;
        factors.push({ factor: `High Service Industry (${servicePct.toFixed(1)}%)`, severity: 'medium', value: `${servicePct.toFixed(1)}%` });
      } else if (servicePct > 55) {
        riskScore += 2;
        factors.push({ factor: `Above Avg Service Ind (${servicePct.toFixed(1)}%)`, severity: 'low', value: `${servicePct.toFixed(1)}%` });
      }
    }

    // 2e. LARGE/SMALL BUSINESS RATIO (Max 3 pts) - lg_sm_biz_ratio
    if (hasValidData(cityItem.lg_sm_biz_ratio)) {
      const bizRatio = safeNumber(cityItem.lg_sm_biz_ratio, 0.5);
      if (bizRatio < 0.2) {
        riskScore += 3;
        factors.push({ factor: `Few Large Employers (${bizRatio.toFixed(2)})`, severity: 'medium', value: `${bizRatio.toFixed(2)} ratio` });
      } else if (bizRatio < 0.35) {
        riskScore += 1;
        factors.push({ factor: `Small Biz Dominant (${bizRatio.toFixed(2)})`, severity: 'low', value: `${bizRatio.toFixed(2)} ratio` });
      }
    }

    // 2f. LOW INCOME HOUSING TRACTS (Max 3 pts) - low_inc_hsg_tract
    if (hasValidData(cityItem.low_inc_hsg_tract)) {
      const lowIncHsgTracts = safeNumber(cityItem.low_inc_hsg_tract, 0);
      if (lowIncHsgTracts > 5) {
        riskScore += 3;
        factors.push({ factor: `Many Low-Inc Hsg Tracts (${lowIncHsgTracts})`, severity: 'medium', value: `${lowIncHsgTracts} tracts` });
      } else if (lowIncHsgTracts > 2) {
        riskScore += 1;
        factors.push({ factor: `Some Low-Inc Hsg (${lowIncHsgTracts})`, severity: 'low', value: `${lowIncHsgTracts} tracts` });
      }
    }

    // =========================================================================
    // 3. INSURANCE & INSTITUTIONAL CAPACITY (Max ~16 pts)
    // =========================================================================

    // 3a. NFIP FLOOD INSURANCE (Max 10 pts) - nfipp (raw %)
    if (hasValidData(cityItem.nfipp)) {
      const nfipPct = safeNumber(cityItem.nfipp, 10);
      if (nfipPct < 5) {
        riskScore += 10;
        factors.push({ factor: `Very Low Flood Ins (${nfipPct.toFixed(1)}%)`, severity: 'critical', value: `${nfipPct.toFixed(1)}%` });
      } else if (nfipPct < 15) {
        riskScore += 6;
        factors.push({ factor: `Low NFIP Coverage (${nfipPct.toFixed(1)}%)`, severity: 'high', value: `${nfipPct.toFixed(1)}%` });
      } else if (nfipPct < 25) {
        riskScore += 3;
        factors.push({ factor: `Below Avg NFIP (${nfipPct.toFixed(1)}%)`, severity: 'medium', value: `${nfipPct.toFixed(1)}%` });
      }
    }

    // 3b. GOVT/SPECIAL DISTRICTS (Max 3 pts) - govt_spec_d_n (raw count)
    if (hasValidData(cityItem.govt_spec_d_n)) {
      const govtDistricts = safeNumber(cityItem.govt_spec_d_n, 5);
      if (govtDistricts < 2) {
        riskScore += 3;
        factors.push({ factor: `Limited Govt Presence (${govtDistricts})`, severity: 'medium', value: `${govtDistricts} districts` });
      } else if (govtDistricts < 4) {
        riskScore += 1;
        factors.push({ factor: `Few Govt Districts (${govtDistricts})`, severity: 'low', value: `${govtDistricts} districts` });
      }
    }

    // 3c. TREASURY OPPORTUNITY ZONES (Max 3 pts) - treas_opp_zone_tract (raw count)
    if (hasValidData(cityItem.treas_opp_zone_tract)) {
      const oppZones = safeNumber(cityItem.treas_opp_zone_tract, 0);
      if (oppZones > 3) {
        riskScore += 3;
        factors.push({ factor: `Multiple Opp Zones (${oppZones})`, severity: 'medium', value: `${oppZones} zones` });
      } else if (oppZones > 0) {
        riskScore += 1;
        factors.push({ factor: `In Opportunity Zone (${oppZones})`, severity: 'low', value: `${oppZones} zones` });
      }
    }

    // =========================================================================
    // 4. VULNERABLE POPULATIONS (Max ~36 pts)
    // =========================================================================

    // 4a. LINGUISTIC ISOLATION (Max 8 pts) - linquis_iso_p (raw %)
    if (hasValidData(cityItem.linquis_iso_p)) {
      const lingIsoPct = safeNumber(cityItem.linquis_iso_p, 5);
      if (lingIsoPct > 15) {
        riskScore += 8;
        factors.push({ factor: `High Linguistic Isolation (${lingIsoPct.toFixed(1)}%)`, severity: 'high', value: `${lingIsoPct.toFixed(1)}%` });
      } else if (lingIsoPct > 8) {
        riskScore += 4;
        factors.push({ factor: `Above Avg Ling Isolation (${lingIsoPct.toFixed(1)}%)`, severity: 'medium', value: `${lingIsoPct.toFixed(1)}%` });
      }
    }

    // 4b. DISABLED POPULATION (Max 8 pts) - abled_p (calculate disabled as 100 - abled)
    if (hasValidData(cityItem.abled_p)) {
      const abledPct = safeNumber(cityItem.abled_p, 85);
      const disabledPct = 100 - abledPct;
      if (disabledPct > 20) {
        riskScore += 8;
        factors.push({ factor: `High Disabled Pop (${disabledPct.toFixed(1)}%)`, severity: 'high', value: `${disabledPct.toFixed(1)}% disabled` });
      } else if (disabledPct > 15) {
        riskScore += 4;
        factors.push({ factor: `Above Avg Disabled (${disabledPct.toFixed(1)}%)`, severity: 'medium', value: `${disabledPct.toFixed(1)}% disabled` });
      }
    }

    // 4c. NURSING HOME POPULATION (Max 6 pts) - nhp_cap_n (raw per capita)
    if (hasValidData(cityItem.nhp_cap_n)) {
      const nhpPerCap = safeNumber(cityItem.nhp_cap_n, 0);
      if (nhpPerCap > 0.02) {
        riskScore += 6;
        factors.push({ factor: `High Nursing Home (${(nhpPerCap * 100).toFixed(2)}%)`, severity: 'high', value: `${(nhpPerCap * 100).toFixed(2)}%` });
      } else if (nhpPerCap > 0.01) {
        riskScore += 3;
        factors.push({ factor: `Above Avg Nursing Home (${(nhpPerCap * 100).toFixed(2)}%)`, severity: 'medium', value: `${(nhpPerCap * 100).toFixed(2)}%` });
      }
    }

    // 4d. ELDERLY POPULATION (Max 6 pts) - non_elder_p (calculate elderly as 100 - non_elder)
    if (hasValidData(cityItem.non_elder_p)) {
      const nonElderPct = safeNumber(cityItem.non_elder_p, 85);
      const elderlyPct = 100 - nonElderPct;
      if (elderlyPct > 25) {
        riskScore += 6;
        factors.push({ factor: `High Elderly Pop (${elderlyPct.toFixed(1)}%)`, severity: 'high', value: `${elderlyPct.toFixed(1)}% elderly` });
      } else if (elderlyPct > 18) {
        riskScore += 3;
        factors.push({ factor: `Above Avg Elderly (${elderlyPct.toFixed(1)}%)`, severity: 'medium', value: `${elderlyPct.toFixed(1)}% elderly` });
      }
    }

    // 4e. FEMALE POPULATION (Max 4 pts) - female_p (raw %)
    if (hasValidData(cityItem.female_p)) {
      const femalePct = safeNumber(cityItem.female_p, 51);
      if (femalePct > 55) {
        riskScore += 4;
        factors.push({ factor: `High Female Pop (${femalePct.toFixed(1)}%)`, severity: 'medium', value: `${femalePct.toFixed(1)}%` });
      } else if (femalePct > 53) {
        riskScore += 2;
        factors.push({ factor: `Above Avg Female (${femalePct.toFixed(1)}%)`, severity: 'low', value: `${femalePct.toFixed(1)}%` });
      }
    }

    // 4f. CIVIC ORGANIZATIONS (Max 4 pts) - civ_10k (raw number per 10K)
    if (hasValidData(cityItem.civ_10k)) {
      const civicOrgs = safeNumber(cityItem.civ_10k, 10);
      if (civicOrgs < 5) {
        riskScore += 4;
        factors.push({ factor: `Limited Civic Support (${civicOrgs.toFixed(1)}/10K)`, severity: 'medium', value: `${civicOrgs.toFixed(1)} orgs/10K` });
      } else if (civicOrgs < 8) {
        riskScore += 2;
        factors.push({ factor: `Below Avg Civic Orgs (${civicOrgs.toFixed(1)}/10K)`, severity: 'low', value: `${civicOrgs.toFixed(1)} orgs/10K` });
      }
    }

    // =========================================================================
    // 5. HOUSING VULNERABILITY (Max ~14 pts)
    // =========================================================================

    // 5a. MOBILE HOMES - REMOVED FROM CALCULATION
    // The non_mobile_p field data appears unreliable. Fort Lee, NJ (07024) shows
    // 37.78% non_mobile which would imply 62% mobile homes, but Fort Lee is a
    // dense urban area with high-rises. Field definition unclear.
    // TODO: Investigate what non_mobile actually measures before re-enabling.

    // 5b. HOMEOWNERSHIP RATE (Max 6 pts) - homeowner_p (raw %)
    if (hasValidData(cityItem.homeowner_p)) {
      const homeownerPct = Math.max(0, Math.min(100, safeNumber(cityItem.homeowner_p, 65) + assumptions.homeownership));
      if (homeownerPct < 35) {
        riskScore += 6;
        factors.push({ factor: `Low Homeownership (${homeownerPct.toFixed(1)}%)`, severity: 'high', value: `${homeownerPct.toFixed(1)}%` });
      } else if (homeownerPct < 50) {
        riskScore += 3;
        factors.push({ factor: `Below Avg Homeownership (${homeownerPct.toFixed(1)}%)`, severity: 'medium', value: `${homeownerPct.toFixed(1)}%` });
      }
    }

    // 5c. AGING HOUSING STOCK (Max 5 pts) - newer_housing (percentile)
    if (hasValidData(cityItem.newer_housing)) {
      const housingPctl = Math.max(0, Math.min(100, safeNumber(cityItem.newer_housing, 50) + assumptions.housingQuality));
      if (housingPctl < 30) {
        riskScore += 5;
        factors.push({ factor: `Very Old Housing (${housingPctl.toFixed(0)}%tile)`, severity: 'high', value: `${housingPctl.toFixed(0)}th %tile newer` });
      } else if (housingPctl < 45) {
        riskScore += 2;
        factors.push({ factor: `Aging Housing (${housingPctl.toFixed(0)}%tile)`, severity: 'medium', value: `${housingPctl.toFixed(0)}th %tile newer` });
      }
    }

    // 5d. HOUSING DENSITY (Max 3 pts) - people_unit_n (raw people per unit)
    if (hasValidData(cityItem.people_unit_n)) {
      const peoplePerUnit = safeNumber(cityItem.people_unit_n, 2.5);
      if (peoplePerUnit > 3.5) {
        riskScore += 3;
        factors.push({ factor: `High Housing Density (${peoplePerUnit.toFixed(2)}/unit)`, severity: 'medium', value: `${peoplePerUnit.toFixed(2)} per unit` });
      }
    }

    // =========================================================================
    // 6. HEALTHCARE & INFRASTRUCTURE (Max ~21 pts)
    // =========================================================================

    // 6a. HOSPITAL BEDS (Max 6 pts) - hosp_bed_10k_n (raw beds per 10K)
    if (hasValidData(cityItem.hosp_bed_10k_n)) {
      const hospBeds = Math.max(0, safeNumber(cityItem.hosp_bed_10k_n, 25) + assumptions.hospitalBeds);
      if (hospBeds < 15) {
        riskScore += 6;
        factors.push({ factor: `Very Low Hospital Beds (${hospBeds.toFixed(1)}/10K)`, severity: 'high', value: `${hospBeds.toFixed(1)} beds/10K` });
      } else if (hospBeds < 25) {
        riskScore += 3;
        factors.push({ factor: `Limited Hospital Beds (${hospBeds.toFixed(1)}/10K)`, severity: 'medium', value: `${hospBeds.toFixed(1)} beds/10K` });
      }
    }

    // 6b. HOSPITALS PER CAPITA (Max 3 pts) - hosp_capita_n (raw per capita)
    if (hasValidData(cityItem.hosp_capita_n)) {
      const hospPerCap = safeNumber(cityItem.hosp_capita_n, 0);
      if (hospPerCap > 0 && hospPerCap < 0.0001) {
        riskScore += 3;
        factors.push({ factor: `Very Few Hospitals (${(hospPerCap * 10000).toFixed(2)}/10K)`, severity: 'medium', value: `${(hospPerCap * 10000).toFixed(2)}/10K` });
      } else if (hospPerCap > 0 && hospPerCap < 0.0003) {
        riskScore += 1;
        factors.push({ factor: `Limited Hospitals (${(hospPerCap * 10000).toFixed(2)}/10K)`, severity: 'low', value: `${(hospPerCap * 10000).toFixed(2)}/10K` });
      }
    }

    // 6c. TELEPHONE ACCESS (Max 4 pts) - tele_p (raw %)
    if (hasValidData(cityItem.tele_p)) {
      const telePct = Math.max(0, Math.min(100, safeNumber(cityItem.tele_p, 95) + assumptions.teleAccess));
      if (telePct < 85) {
        riskScore += 4;
        factors.push({ factor: `Poor Communication (${telePct.toFixed(1)}%)`, severity: 'high', value: `${telePct.toFixed(1)}%` });
      } else if (telePct < 92) {
        riskScore += 2;
        factors.push({ factor: `Limited Communication (${telePct.toFixed(1)}%)`, severity: 'medium', value: `${telePct.toFixed(1)}%` });
      }
    }

    // 6d. VEHICLE ACCESS (Max 4 pts) - vehicle (percentile)
    if (hasValidData(cityItem.vehicle)) {
      const vehiclePctl = Math.max(0, Math.min(100, safeNumber(cityItem.vehicle, 60) + assumptions.vehicleAccess));
      if (vehiclePctl < 40) {
        riskScore += 4;
        factors.push({ factor: `Very Low Vehicle Access (${vehiclePctl.toFixed(0)}%tile)`, severity: 'high', value: `${vehiclePctl.toFixed(0)}%tile` });
      } else if (vehiclePctl < 55) {
        riskScore += 2;
        factors.push({ factor: `Limited Vehicle Access (${vehiclePctl.toFixed(0)}%tile)`, severity: 'medium', value: `${vehiclePctl.toFixed(0)}%tile` });
      }
    }

    // 6e. SHELTER INFRASTRUCTURE (Max 4 pts) - hotels_km_n, pub_schls_mi_n (raw density)
    const hotelDensity = safeNumber(cityItem.hotels_km_n, 0.5);
    const schoolDensity = safeNumber(cityItem.pub_schls_mi_n, 1);
    if (hasValidData(cityItem.hotels_km_n) || hasValidData(cityItem.pub_schls_mi_n)) {
      if (hotelDensity < 0.1 && schoolDensity < 0.5) {
        riskScore += 4;
        factors.push({ factor: `Very Limited Shelters (${hotelDensity.toFixed(2)} hotels/kmÂ²)`, severity: 'high', value: `${hotelDensity.toFixed(2)} hotels/kmÂ²` });
      } else if (hotelDensity < 0.3 && schoolDensity < 1) {
        riskScore += 2;
        factors.push({ factor: `Limited Shelters (${hotelDensity.toFixed(2)} hotels/kmÂ²)`, severity: 'medium', value: `${hotelDensity.toFixed(2)} hotels/kmÂ²` });
      }
    }

    // =========================================================================
    // 7. DISASTER EXPERIENCE BONUS (Reduces risk)
    // =========================================================================
    if (hasValidData(cityItem.pd_dis_dec_n)) {
      const disasterDecl = safeNumber(cityItem.pd_dis_dec_n, 0);
      if (disasterDecl >= 10) {
        riskScore = Math.max(0, riskScore - 8);
        factors.push({ factor: `Extensive Disaster Exp (${disasterDecl} decl)`, severity: 'low', value: `${disasterDecl} declarations (-8 pts)` });
      } else if (disasterDecl >= 5) {
        riskScore = Math.max(0, riskScore - 5);
        factors.push({ factor: `High Disaster Exp (${disasterDecl} decl)`, severity: 'low', value: `${disasterDecl} declarations (-5 pts)` });
      }
    }

    // =========================================================================
    // FINALIZATION
    // =========================================================================

    // Sort factors by severity
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    factors.sort((a, b) => severityOrder[a.severity as keyof typeof severityOrder] - severityOrder[b.severity as keyof typeof severityOrder]);

    // Cap at 100, categorize
    const finalScore = Math.min(riskScore, 100);
    
    return {
      riskScore: finalScore,
      factors,
      recoveryCategory: finalScore >= 70 ? 'unlikely' : 
                        finalScore >= 50 ? 'difficult' : 
                        finalScore >= 30 ? 'challenging' : 'expected'
    };
  };

  // ============================================================================
  // METHODOLOGY DIALOG
  // ============================================================================

  const MethodologyDialog = () => (
    <Dialog open={methodologyOpen} onClose={() => setMethodologyOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MethodologyIcon />
        <Typography variant="h6">People Metrics Methodology</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Data Sources</Typography>
        <Typography variant="body2" paragraph>
          People metrics are derived from U.S. Census data, FEMA community resilience indicators, 
          healthcare facility databases, NFIP flood insurance records, and infrastructure assessments at the postal code level.
          Over 50 data points per location covering demographics, economics, infrastructure, and institutional capacity.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>City-Level Aggregation:</strong> When multiple postal codes exist within the same city, 
            community-level metrics are averaged across all locations to provide a more accurate picture of the broader community's recovery capacity.
          </Typography>
        </Alert>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>Disaster Recovery Risk Score (0-100)</Typography>
        <Typography variant="body2" paragraph>
          The Recovery Risk Score measures factors that affect recovery <strong>AFTER</strong> a disaster occurs.
          It focuses on on-the-ground socioeconomic, demographic, infrastructure, and institutional factors
          that determine how quickly and effectively a community can recover. Climate risk (likelihood of disaster)
          is shown separately in the Locations Analysis tab.
        </Typography>
        <Typography variant="body2" paragraph>
          Uses <strong>27 factors</strong> across 7 categories using <strong>raw percentages and values</strong>. 
          Only valid data (non-zero values) triggers risk flags. Maximum possible score: ~147 points (capped at 100).
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Reading Values:</strong> Most factors use raw percentages (e.g., "27.8% elderly") or raw counts 
            (e.g., "15 beds/10K"). For "non_" fields, we calculate the inverse (e.g., non_elder_p of 72% â†’ 28% elderly).
          </Typography>
        </Alert>

        <Grid container spacing={2}>
          {/* Column 1 */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 'bold', color: '#7b1fa2' }}>Race & Ethnicity Factors (Max 24 pts)</Typography>
            <List dense sx={{ bgcolor: '#f3e5f5', borderRadius: 1, mb: 2 }}>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="People of Color (8 pts)" secondary="> 60% adds 8; > 40% adds 4" />
              </ListItem>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Black Population (6 pts)" secondary="> 40% adds 6; > 25% adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Native American (6 pts)" secondary="> 15% adds 6; > 5% adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Hispanic Population (4 pts)" secondary="> 50% adds 4; > 30% adds 2" />
              </ListItem>
            </List>

            <Typography variant="subtitle2" color="warning.dark" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>Economic Vulnerability (Max 35 pts)</Typography>
            <List dense sx={{ bgcolor: '#fff3e0', borderRadius: 1, mb: 2 }}>
              <ListItem>
                <ListItemIcon><WarningIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText primary="Low-Income Population (12 pts)" secondary="> 40% adds 12; > 25% adds 7; > 15% adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><HomeIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText primary="Lower Property Value (8 pts)" secondary="< $1M adds 8; $1-5M adds 4" />
              </ListItem>
              <ListItem>
                <ListItemIcon><TrendingUpIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText primary="Economic Diversity (5 pts)" secondary="< 15% creative class & < 85% non-agri" />
              </ListItem>
              <ListItem>
                <ListItemIcon><TrendingUpIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText primary="Service Industry (4 pts)" secondary="> 70% adds 4; > 55% adds 2" />
              </ListItem>
              <ListItem>
                <ListItemIcon><TrendingUpIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText primary="Large/Small Biz Ratio (3 pts)" secondary="< 0.2 adds 3; < 0.35 adds 1" />
              </ListItem>
              <ListItem>
                <ListItemIcon><HomeIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText primary="Low-Inc Housing Tracts (3 pts)" secondary="> 5 tracts adds 3; > 2 adds 1" />
              </ListItem>
            </List>

            <Typography variant="subtitle2" color="info.dark" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>Insurance & Institutional (Max 16 pts)</Typography>
            <List dense sx={{ bgcolor: '#e3f2fd', borderRadius: 1, mb: 2 }}>
              <ListItem>
                <ListItemIcon><SecurityIcon fontSize="small" color="info" /></ListItemIcon>
                <ListItemText primary="NFIP Flood Insurance (10 pts)" secondary="< 5% adds 10; < 15% adds 6; < 25% adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><SecurityIcon fontSize="small" color="info" /></ListItemIcon>
                <ListItemText primary="Govt/Special Districts (3 pts)" secondary="< 2 districts adds 3; < 4 adds 1" />
              </ListItem>
              <ListItem>
                <ListItemIcon><SecurityIcon fontSize="small" color="info" /></ListItemIcon>
                <ListItemText primary="Opportunity Zones (3 pts)" secondary="> 3 zones adds 3; > 0 adds 1" />
              </ListItem>
            </List>
          </Grid>

          {/* Column 2 */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="secondary.dark" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>Vulnerable Populations (Max 36 pts)</Typography>
            <List dense sx={{ bgcolor: '#fce4ec', borderRadius: 1, mb: 2 }}>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Linguistic Isolation (8 pts)" secondary="> 15% adds 8; > 8% adds 4" />
              </ListItem>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Disabled Population (8 pts)" secondary="> 20% disabled adds 8; > 15% adds 4" />
              </ListItem>
              <ListItem>
                <ListItemIcon><HospitalIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Nursing Home Pop (6 pts)" secondary="> 2% per capita adds 6; > 1% adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Elderly Population (6 pts)" secondary="> 25% elderly adds 6; > 18% adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Female Population (4 pts)" secondary="> 55% adds 4; > 53% adds 2" />
              </ListItem>
              <ListItem>
                <ListItemIcon><GroupsIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText primary="Civic Organizations (4 pts)" secondary="< 5 orgs/10K adds 4; < 8 adds 2" />
              </ListItem>
            </List>

            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 'bold', color: '#546e7a' }}>Housing Vulnerability (Max 14 pts)</Typography>
            <List dense sx={{ bgcolor: '#eceff1', borderRadius: 1, mb: 2 }}>
              <ListItem>
                <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Homeownership Rate (6 pts)" secondary="< 35% adds 6; < 50% adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Aging Housing (5 pts)" secondary="< 30th %tile newer adds 5; < 45th adds 2" />
              </ListItem>
              <ListItem>
                <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Housing Density (3 pts)" secondary="> 3.5 people/unit adds 3" />
              </ListItem>
            </List>

            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 'bold', color: '#1565c0' }}>Healthcare & Infrastructure (Max 21 pts)</Typography>
            <List dense sx={{ bgcolor: '#e3f2fd', borderRadius: 1, mb: 2 }}>
              <ListItem>
                <ListItemIcon><HospitalIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary="Hospital Beds (6 pts)" secondary="< 15 beds/10K adds 6; < 25 adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><HospitalIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary="Hospitals Per Capita (3 pts)" secondary="< 0.0001 per capita adds 3" />
              </ListItem>
              <ListItem>
                <ListItemIcon><PhoneIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary="Telephone Access (4 pts)" secondary="< 85% adds 4; < 92% adds 2" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CarIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary="Vehicle Access (4 pts)" secondary="< 40th %tile adds 4; < 55th adds 2" />
              </ListItem>
              <ListItem>
                <ListItemIcon><HomeIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary="Shelter Infrastructure (4 pts)" secondary="< 0.1 hotels/kmÂ² & < 0.5 schools/mi adds 4" />
              </ListItem>
            </List>
          </Grid>
        </Grid>

        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Disaster Experience Bonus:</strong> Communities with 10+ FEMA disaster declarations receive -8 points; 
            5-9 declarations receive -5 points, reflecting their experience navigating recovery processes.
          </Typography>
        </Alert>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Data Quality:</strong> Risk factors only trigger when valid data exists. Missing or zero values are NOT penalized.
            The actual values shown in each factor label help you understand why a flag was triggered.
          </Typography>
        </Alert>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight="bold">Recovery Categories</Typography>
          <Typography variant="body2">
             <strong style={{ color: '#d32f2f' }}>Unlikely (70-100)</strong>: Severe deficiencies - critical intervention needed<br/>
             <strong style={{ color: '#ed6c02' }}>Difficult (50-69)</strong>: Significant challenges - requires substantial support<br/>
             <strong style={{ color: '#f9a825' }}>Challenging (30-49)</strong>: Moderate challenges - monitor closely<br/>
             <strong style={{ color: '#2e7d32' }}>Expected (&lt;30)</strong>: Good resilience - likely to recover
          </Typography>
        </Alert>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>Research Basis</Typography>
        
        <Typography variant="subtitle2" color="error" sx={{ mt: 1 }}>Race & Disaster Recovery Disparities</Typography>
        <Typography variant="body2" component="div" sx={{ pl: 2, mb: 1 }}>
           <strong>FEMA Funding Gap:</strong> Black neighborhoods receive 5-10% less FEMA funding than white neighborhoods with same damage levels (Center for American Progress)<br/>
           <strong>Application Denial Rates:</strong> 11% denial rate in predominantly Black neighborhoods vs. 4% in white neighborhoods<br/>
           <strong>Native American Infrastructure:</strong> Reservations face severe infrastructure gaps, limited healthcare, and federal coordination challenges<br/>
           <strong>Hispanic Barriers:</strong> Language barriers in FEMA applications, documentation concerns limit aid access<br/>
           <strong>Wealth Disparity:</strong> White residents in disaster-struck counties see wealth increase 5x more than non-white residents post-disaster
        </Typography>

        <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Economic Vulnerability</Typography>
        <Typography variant="body2" component="div" sx={{ pl: 2, mb: 1 }}>
           <strong>Insurance Gap:</strong> Only 17% of homeowners in Harvey-affected low-income areas held flood insurance (Brookings)<br/>
           <strong>FEMA Aid Disparity:</strong> Low-income/minority counties receive less FEMA funding even with same damage (CAP)<br/>
           <strong>Opportunity Zones:</strong> Designated areas indicate pre-existing economic distress, compounding recovery challenges<br/>
           <strong>Service Industry:</strong> High service-sector dependence means more low-wage jobs vulnerable to business closures
        </Typography>

        <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Vulnerable Populations</Typography>
        <Typography variant="body2" component="div" sx={{ pl: 2, mb: 1 }}>
           <strong>Linguistic Isolation:</strong> Communication barriers prevent timely evacuation warnings and FEMA application assistance<br/>
           <strong>Disability:</strong> Requires additional evacuation assistance and accessible temporary housing<br/>
           <strong>Nursing Homes:</strong> Require coordinated medical evacuations; Hurricane Irma nursing home deaths highlighted risks<br/>
           <strong>Female-Headed Households:</strong> Research shows single mothers face unique recovery barriers including childcare and income loss
        </Typography>

        <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Housing Vulnerability</Typography>
        <Typography variant="body2" component="div" sx={{ pl: 2, mb: 1 }}>
           <strong>Mobile Homes:</strong> Suffer catastrophic damage at lower wind speeds; FEMA reports 44% of hurricane deaths occur in mobile homes<br/>
           <strong>Renters:</strong> Face displacement, no FEMA home repair aid, and landlords may not rebuild (Katrina rental housing crisis)
        </Typography>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Sources:</strong> Brookings Institution  Center for American Progress  Illinois Tech/NSF  
            FEMA NRI Documentation  NBER Working Papers  CDC Morbidity and Mortality Reports 
            Journal of the American Planning Association  Russell Sage Foundation
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setMethodologyOpen(false)} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );

  // ============================================================================
  // DASHBOARD OVERVIEW COMPONENT (Fixed 2 decimal places)
  // ============================================================================
  
  const DashboardOverview = () => {
    if (!aggregatedMetrics) return null;

    const resilienceScore = Number(aggregatedMetrics.resilience?.avg) || 0;
    const vulnerabilityScore = Number(aggregatedMetrics.com_risk?.avg) || 0;
    const homeownershipRate = Number(aggregatedMetrics.homeowner_p?.avg) || 0;
    const hospitalBeds = Number(aggregatedMetrics.hosp_bed_10k_n?.avg) || 0;
    const totalAssetValue = Number(aggregatedMetrics.asset_value?.sum) || 0;

    // Build chart data - with 2 decimal places
    const categoryData = [
      { category: 'Working Age Pop', score: Number(aggregatedMetrics.non_elder?.avg) || 0 },
      { category: 'Housing', score: Number(aggregatedMetrics.newer_housing?.avg) || 0 },
      { category: 'Hospital Capacity', score: Number(aggregatedMetrics.hosp_bed_10k?.avg) || 0 },
      { category: 'Civic Engagement', score: Number(aggregatedMetrics.civ_10k?.avg) || 0 },
    ].filter(item => item.score > 0);

    const distributionData = [
      { name: 'High Resilience (>6)', value: selectedItems.filter(item => safeNumber(item.resilience) > 6).length, color: '#2e7d32' },
      { name: 'Medium Resilience (3-6)', value: selectedItems.filter(item => {
        const res = safeNumber(item.resilience);
        return res > 3 && res <= 6;
      }).length, color: '#1976d2' },
      { name: 'Low Resilience (0-3)', value: selectedItems.filter(item => safeNumber(item.resilience) <= 3).length, color: '#d32f2f' },
    ];

    const COLORS = ['#2e7d32', '#1976d2', '#d32f2f'];

    // Get risk level color based on platform thresholds
    const getRiskColor = (score: number, inverse: boolean = false): string => {
      if (inverse) {
        // For resilience: higher is better
        if (score > 6) return '#2e7d32'; // Green
        if (score > 3) return '#f9a825'; // Amber
        return '#d32f2f'; // Red
      } else {
        // For vulnerability: lower is better
        if (score > 6) return '#d32f2f'; // Red
        if (score > 3) return '#f9a825'; // Amber
        return '#2e7d32'; // Green
      }
    };

    return (
      <Box>
        {/* Total Asset Value Banner */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fffde7', border: '1px solid #fbc02d' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon sx={{ fontSize: 32, color: '#f9a825' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Real Estate Value</Typography>
                  <Typography variant="h4" fontWeight="bold">{formatCurrency(totalAssetValue)}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                <strong>{selectedItems.length}</strong> locations analyzed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg per location: <strong>{formatCurrency(totalAssetValue / selectedItems.length)}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Min: {formatCurrency(aggregatedMetrics.asset_value?.min || 0)} | 
                Max: {formatCurrency(aggregatedMetrics.asset_value?.max || 0)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SecurityIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">Avg Resilience</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold" sx={{ color: getRiskColor(resilienceScore, true) }}>
                  {resilienceScore.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">out of 10</Typography>
                <Chip 
                  label={resilienceScore > 6 ? 'Low Risk' : resilienceScore > 3 ? 'Medium Risk' : 'High Risk'} 
                  size="small" 
                  sx={{ mt: 1, bgcolor: getRiskColor(resilienceScore, true), color: 'white' }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="subtitle2" color="text.secondary">Avg Vulnerability</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold" sx={{ color: getRiskColor(vulnerabilityScore) }}>
                  {vulnerabilityScore.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">out of 10</Typography>
                <Chip 
                  label={vulnerabilityScore > 6 ? 'High Risk' : vulnerabilityScore > 3 ? 'Medium Risk' : 'Low Risk'} 
                  size="small" 
                  sx={{ mt: 1, bgcolor: getRiskColor(vulnerabilityScore), color: 'white' }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <HomeIcon color="info" />
                  <Typography variant="subtitle2" color="text.secondary">Avg Homeownership</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {homeownershipRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">of housing units</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <HospitalIcon color="error" />
                  <Typography variant="subtitle2" color="text.secondary">Avg Hospital Beds</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {hospitalBeds.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">per 10,000 population</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Resilience Distribution Pie */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Resilience Distribution</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Based on platform thresholds: High (&gt;6), Medium (3-6), Low (0-3)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={distributionData.filter(d => d.value > 0)}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {distributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Category Percentiles */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Community Category Percentiles</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="category" type="category" width={120} />
                  <RechartsTooltip formatter={(value: number) => [`${value.toFixed(1)}%ile`, 'Percentile']} />
                  <Bar dataKey="score" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // ============================================================================
  // DETAILED METRICS TABLE
  // ============================================================================

  const DetailedMetricsTable = () => {
    if (!aggregatedMetrics) return <Typography>No data available</Typography>;

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Metric</strong></TableCell>
              <TableCell align="center"><strong>Total / Average</strong></TableCell>
              <TableCell align="center"><strong>Min</strong></TableCell>
              <TableCell align="center"><strong>Max</strong></TableCell>
              <TableCell align="center"><strong>Locations</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {METRICS_CONFIG.map(config => {
              const metric = aggregatedMetrics[config.column_name];
              if (!metric || metric.count === 0) return null;
              
              // For asset_value, show total sum; for others show average
              const isCurrency = config.format === 'currency';
              const displayValue = isCurrency ? metric.sum : metric.avg;
              const displayLabel = isCurrency ? 'Total' : 'Avg';
              
              return (
                <TableRow key={config.column_name} hover sx={isCurrency ? { bgcolor: '#fffde7' } : {}}>
                  <TableCell>
                    <Tooltip title={config.tooltip}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {config.icon}
                        <Typography variant="body2">{config.alias}</Typography>
                        {isCurrency && <Chip label="Financial" size="small" color="warning" sx={{ ml: 1, height: 20 }} />}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{formatMetricValue(displayValue, config.format)}</Typography>
                      <Typography variant="caption" color="text.secondary">{displayLabel}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{formatMetricValue(metric.min, config.format)}</TableCell>
                  <TableCell align="center">{formatMetricValue(metric.max, config.format)}</TableCell>
                  <TableCell align="center">{metric.count}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ============================================================================
  // LOCATION ANALYSIS (Comparative) - AGGREGATED BY CITY
  // ============================================================================

  const LocationAnalysis = () => {
    // Aggregate locations by city, averaging metrics and summing asset values
    const aggregatedByCity = useMemo(() => {
      const cityMap = new Map<string, {
        city: string;
        state: string;
        resilience: number[];
        com_risk: number[];
        asset_value: number;
        locationCount: number;
      }>();

      selectedItems.forEach(item => {
        const key = `${item.city?.toLowerCase() || 'unknown'}-${item.state?.toLowerCase() || ''}`;
        const existing = cityMap.get(key);
        
        if (existing) {
          existing.resilience.push(safeNumber(item.resilience));
          existing.com_risk.push(safeNumber(item.com_risk));
          existing.asset_value += parseAssetValue(item.asset_value);
          existing.locationCount += 1;
        } else {
          cityMap.set(key, {
            city: item.city || 'Unknown',
            state: item.state || '',
            resilience: [safeNumber(item.resilience)],
            com_risk: [safeNumber(item.com_risk)],
            asset_value: parseAssetValue(item.asset_value),
            locationCount: 1
          });
        }
      });

      // Convert to array with averaged values
      return Array.from(cityMap.values()).map(city => ({
        city: city.city,
        state: city.state,
        resilience: city.resilience.reduce((a, b) => a + b, 0) / city.resilience.length,
        com_risk: city.com_risk.reduce((a, b) => a + b, 0) / city.com_risk.length,
        asset_value: city.asset_value,
        locationCount: city.locationCount
      }));
    }, [selectedItems]);

    const sortedByResilience = useMemo(() => 
      [...aggregatedByCity].sort((a, b) => b.resilience - a.resilience).slice(0, 10),
      [aggregatedByCity]
    );

    const sortedByVulnerability = useMemo(() => 
      [...aggregatedByCity].sort((a, b) => b.com_risk - a.com_risk).slice(0, 10),
      [aggregatedByCity]
    );

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32' }}>
                <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Top 10 Most Resilient Cities
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell align="center">Locations</TableCell>
                      <TableCell align="right">Avg Resilience</TableCell>
                      <TableCell align="right">Total Asset Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedByResilience.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell><Chip label={`#${idx + 1}`} size="small" color="success" /></TableCell>
                        <TableCell>{item.city}, {item.state}</TableCell>
                        <TableCell align="center">
                          {item.locationCount > 1 && (
                            <Chip label={item.locationCount} size="small" variant="outlined" />
                          )}
                          {item.locationCount === 1 && '1'}
                        </TableCell>
                        <TableCell align="right"><strong>{item.resilience.toFixed(2)}</strong></TableCell>
                        <TableCell align="right">{formatCurrency(item.asset_value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#d32f2f' }}>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Top 10 Most Vulnerable Cities
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell align="center">Locations</TableCell>
                      <TableCell align="right">Avg Vulnerability</TableCell>
                      <TableCell align="right">Total Asset Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedByVulnerability.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell><Chip label={`#${idx + 1}`} size="small" color="error" /></TableCell>
                        <TableCell>{item.city}, {item.state}</TableCell>
                        <TableCell align="center">
                          {item.locationCount > 1 && (
                            <Chip label={item.locationCount} size="small" variant="outlined" />
                          )}
                          {item.locationCount === 1 && '1'}
                        </TableCell>
                        <TableCell align="right"><strong>{item.com_risk.toFixed(2)}</strong></TableCell>
                        <TableCell align="right">{formatCurrency(item.asset_value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // ============================================================================
  // DISASTER RECOVERY MODEL (Enhanced with What-If + Location Lists)
  // ============================================================================

  const DisasterRecoveryModel = () => {
    const itemsWithRisk = useMemo(() => 
      selectedItems.map(item => ({
        ...item,
        recovery: calculateRecoveryRisk(item, showWhatIf ? whatIfAssumptions : DEFAULT_ASSUMPTIONS)
      })).sort((a, b) => b.recovery.riskScore - a.recovery.riskScore),
      [selectedItems, showWhatIf, whatIfAssumptions]
    );

    const unlikelyToRecover = itemsWithRisk.filter(i => i.recovery.recoveryCategory === 'unlikely');
    const difficultRecovery = itemsWithRisk.filter(i => i.recovery.recoveryCategory === 'difficult');
    const challengingRecovery = itemsWithRisk.filter(i => i.recovery.recoveryCategory === 'challenging');
    const expectedRecovery = itemsWithRisk.filter(i => i.recovery.recoveryCategory === 'expected');

    // Calculate asset values using proper parsing
    const calcAssetTotal = (items: typeof itemsWithRisk) => 
      items.reduce((sum, item) => sum + parseAssetValue(item.asset_value), 0);

    const recoveryDistribution = [
      { name: 'Unlikely', value: unlikelyToRecover.length, color: '#d32f2f', assetValue: calcAssetTotal(unlikelyToRecover) },
      { name: 'Difficult', value: difficultRecovery.length, color: '#ed6c02', assetValue: calcAssetTotal(difficultRecovery) },
      { name: 'Challenging', value: challengingRecovery.length, color: '#f9a825', assetValue: calcAssetTotal(challengingRecovery) },
      { name: 'Expected', value: expectedRecovery.length, color: '#2e7d32', assetValue: calcAssetTotal(expectedRecovery) },
    ];

    const renderCategoryLocations = (category: string, items: typeof itemsWithRisk, color: string) => (
      <Paper sx={{ mb: 2 }}>
        <Box 
          sx={{ p: 2, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: `${color}15` }}
          onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip label={items.length} sx={{ bgcolor: color, color: 'white', fontWeight: 'bold' }} />
            <Typography variant="subtitle1" fontWeight="bold">{category} Recovery</Typography>
            <Typography variant="body2" color="text.secondary">
              Asset Value: {formatCurrency(calcAssetTotal(items))}
            </Typography>
          </Box>
          <IconButton size="small">
            {expandedCategory === category ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={expandedCategory === category}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Location</TableCell>
                  <TableCell align="center">Risk Score</TableCell>
                  <TableCell align="right">Asset Value</TableCell>
                  <TableCell>Key Risk Factors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">No locations in this category</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.slice(0, 20).map((item, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{item.city || 'Unknown'}, {item.state || ''}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.postal_code || ''}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={item.recovery.riskScore.toFixed(0)} 
                          size="small" 
                          sx={{ bgcolor: color, color: 'white', fontWeight: 'bold', minWidth: 40 }} 
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={parseAssetValue(item.asset_value) > 0 ? 'bold' : 'normal'}>
                          {formatCurrency(item.asset_value)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {item.recovery.factors.slice(0, 3).map((f, i) => (
                            <Tooltip key={i} title={`Value: ${f.value}`}>
                              <Chip 
                                label={f.factor} 
                                size="small" 
                                variant="outlined" 
                                sx={{ 
                                  fontSize: '0.7rem',
                                  borderColor: f.severity === 'critical' ? '#d32f2f' : 
                                              f.severity === 'high' ? '#ed6c02' : '#f9a825',
                                  color: f.severity === 'critical' ? '#d32f2f' : 
                                         f.severity === 'high' ? '#ed6c02' : '#92400e',
                                }} 
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {items.length > 20 && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
              Showing 20 of {items.length} locations
            </Typography>
          )}
        </Collapse>
      </Paper>
    );

    return (
      <Box>
        <Alert severity="error" icon={<DangerousIcon />} sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">Disaster Recovery Risk Assessment</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Identifies locations that may struggle to recover from major disasters based on climate risk, resilience, vulnerability, infrastructure, and demographic factors.
            Uses platform-wide thresholds: High (&gt;6), Medium (3.1-6), Low (0-3).
          </Typography>
        </Alert>

        {/* What-If Scenario Panel */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: showWhatIf ? '#e3f2fd' : '#f5f5f5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: showWhatIf ? 2 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SettingsIcon color={showWhatIf ? 'primary' : 'disabled'} />
              <Typography variant="h6">What-If Scenario Analysis</Typography>
            </Box>
            <Button 
              variant={showWhatIf ? 'contained' : 'outlined'} 
              startIcon={<PlayArrowIcon />}
              onClick={() => setShowWhatIf(!showWhatIf)}
            >
              {showWhatIf ? 'Scenario Active' : 'Enable What-If'}
            </Button>
          </Box>

          <Collapse in={showWhatIf}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Adjust assumptions to see how changes in community factors would affect recovery projections.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" fontWeight="bold">Resilience Adjustment: {whatIfAssumptions.resilience > 0 ? '+' : ''}{whatIfAssumptions.resilience}</Typography>
                <Slider value={whatIfAssumptions.resilience} onChange={(_, v) => setWhatIfAssumptions({...whatIfAssumptions, resilience: v as number})} min={-5} max={5} step={0.5} marks={[{value: -5, label: '-5'}, {value: 0, label: '0'}, {value: 5, label: '+5'}]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" fontWeight="bold">Vulnerability Adjustment: {whatIfAssumptions.vulnerability > 0 ? '+' : ''}{whatIfAssumptions.vulnerability}</Typography>
                <Slider value={whatIfAssumptions.vulnerability} onChange={(_, v) => setWhatIfAssumptions({...whatIfAssumptions, vulnerability: v as number})} min={-5} max={5} step={0.5} marks={[{value: -5, label: '-5'}, {value: 0, label: '0'}, {value: 5, label: '+5'}]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" fontWeight="bold">Homeownership %: {whatIfAssumptions.homeownership > 0 ? '+' : ''}{whatIfAssumptions.homeownership}%</Typography>
                <Slider value={whatIfAssumptions.homeownership} onChange={(_, v) => setWhatIfAssumptions({...whatIfAssumptions, homeownership: v as number})} min={-20} max={20} step={1} marks={[{value: -20, label: '-20%'}, {value: 0, label: '0'}, {value: 20, label: '+20%'}]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" fontWeight="bold">Hospital Beds/10K: {whatIfAssumptions.hospitalBeds > 0 ? '+' : ''}{whatIfAssumptions.hospitalBeds}</Typography>
                <Slider value={whatIfAssumptions.hospitalBeds} onChange={(_, v) => setWhatIfAssumptions({...whatIfAssumptions, hospitalBeds: v as number})} min={-15} max={15} step={1} marks={[{value: -15, label: '-15'}, {value: 0, label: '0'}, {value: 15, label: '+15'}]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" fontWeight="bold">Housing Quality %tile: {whatIfAssumptions.housingQuality > 0 ? '+' : ''}{whatIfAssumptions.housingQuality}</Typography>
                <Slider value={whatIfAssumptions.housingQuality} onChange={(_, v) => setWhatIfAssumptions({...whatIfAssumptions, housingQuality: v as number})} min={-30} max={30} step={5} marks={[{value: -30, label: '-30'}, {value: 0, label: '0'}, {value: 30, label: '+30'}]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Button variant="outlined" onClick={() => setWhatIfAssumptions(DEFAULT_ASSUMPTIONS)} fullWidth>
                    Reset to Baseline
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* Summary Statistics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {recoveryDistribution.map((cat) => (
            <Grid item xs={6} md={3} key={cat.name}>
              <Card sx={{ bgcolor: `${cat.color}10`, height: '100%' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">{cat.name.toUpperCase()} RECOVERY</Typography>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: cat.color }}>{cat.value}</Typography>
                  <Typography variant="body2" color="text.secondary">locations</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" fontWeight="bold">Asset Value: {formatCurrency(cat.assetValue)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Distribution Chart */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Recovery Category Distribution</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={recoveryDistribution} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <RechartsTooltip 
                formatter={(value: number, name: string) => [value, name === 'value' ? 'Locations' : name]}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <Box sx={{ bgcolor: 'white', p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle2">{data.name} Recovery</Typography>
                        <Typography variant="body2">Locations: {data.value}</Typography>
                        <Typography variant="body2">Asset Value: {formatCurrency(data.assetValue)}</Typography>
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="value" name="Locations">
                {recoveryDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        {/* Location Lists by Category */}
        <Typography variant="h6" sx={{ mb: 2 }}>Locations by Recovery Category</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click each category to see the locations within that recovery classification. Sorted by risk score (highest first).
        </Typography>

        {renderCategoryLocations('Unlikely', unlikelyToRecover, '#d32f2f')}
        {renderCategoryLocations('Difficult', difficultRecovery, '#ed6c02')}
        {renderCategoryLocations('Challenging', challengingRecovery, '#f9a825')}
        {renderCategoryLocations('Expected', expectedRecovery, '#2e7d32')}

        {/* Recommendations */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold">Mitigation Recommendations</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
             Review insurance coverage for high-risk locations.  Partner with local emergency services. 
            Prioritize infrastructure improvements.  Consider geographic diversification.
          </Typography>
        </Alert>
      </Box>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (portfolios.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          No portfolios found. Please create and analyze a portfolio first to view People metrics.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <GroupsIcon sx={{ fontSize: 40, color: '#1976d2' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">People Metrics</Typography>
              <Typography variant="body2" color="text.secondary">Community resilience and disaster recovery analysis</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<MethodologyIcon />} onClick={() => setMethodologyOpen(true)}>Methodology</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportToCSV}>Export Data</Button>
          </Box>
        </Box>

        {/* Portfolio Selector */}
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Select Portfolio</InputLabel>
          <Select value={selectedPortfolio} label="Select Portfolio" onChange={(e) => setSelectedPortfolio(e.target.value)}>
            <MenuItem value="all">All Portfolios ({portfolios.length})</MenuItem>
            {portfolios.map(portfolio => (
              <MenuItem key={portfolio.id} value={portfolio.id}>
                {portfolio.name} ({portfolio.items?.length || 0} locations)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Summary Stats */}
        <Paper sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Total Locations</Typography>
              <Typography variant="h6" fontWeight="bold">{selectedItems.length}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Average Resilience</Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {aggregatedMetrics?.resilience ? `${Number(aggregatedMetrics.resilience.avg).toFixed(2)} / 10` : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Average Vulnerability</Typography>
              <Typography variant="h6" fontWeight="bold" color="error.main">
                {aggregatedMetrics?.com_risk ? `${Number(aggregatedMetrics.com_risk.avg).toFixed(2)} / 10` : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Dashboard" icon={<DashboardIcon />} iconPosition="start" />
          <Tab label="Metrics Table" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Location Analysis" icon={<MapIcon />} iconPosition="start" />
          <Tab label="Disaster Recovery Risk" icon={<DangerousIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}><DashboardOverview /></TabPanel>
          <TabPanel value={activeTab} index={1}><DetailedMetricsTable /></TabPanel>
          <TabPanel value={activeTab} index={2}><LocationAnalysis /></TabPanel>
          <TabPanel value={activeTab} index={3}><DisasterRecoveryModel /></TabPanel>
        </Box>
      </Paper>

      <MethodologyDialog />

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Container>
  );
}